import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { createElement, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { WithAuthorization, useAuth } from "../../../context";
import { RoundOverrideProvider } from "../../../context/RoundOverrideContext";
import { routes } from "../../../config/paths";
import { showErrorToast, USER_ROLES } from "../../../utils";
import { H2Dashboard } from "../../../components/text/headings";
import {
  foundationV2Keys,
  issbKeys,
  useAddFoundationV2EntityToYear,
  useCreateFoundationV2Year,
  useFoundationV2Round,
  useFoundationV2Years,
  useGetOrganizationEntities,
  useInitFoundationV2Round,
} from "hooks/query";
import { useGetQuestionsOfRound } from "../../../hooks/query";
import IssbContainer from "../IssbContainer";
import SecondaryHeader from "../components/SecondaryHeader";
import AddYearControl from "./components/AddYearControl";
import EmptyState from "./components/EmptyState";
import FoundationV2SetupController from "./components/FoundationV2SetupController";
import SectionGrid from "./components/SectionGrid";
import FilterPill from "./components/FilterPill";
import { FOUNDATION_SECTION_ICONS, groupQuestionsBySection, yearOfQuestion } from "./foundationTypes";
import {
  chooseEntity,
  entityOptions,
  questionBelongsToEntity,
  yearsMissingForEntity,
} from "../../../utils/foundationEntities";

/** Roles holding the backend's write authority (`update:organization-questionnaires-data`). */
const WRITE_ROLES = [USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MEMBER, "ESG_ADMIN"];

/**
 * Foundation Data v2 — the reporting years, and the three workstreams each one is divided into, per entity.
 *
 * Every workstream belongs to an entity (Settings → Entities), so the page shows one entity at a time, chosen
 * in a selector and kept in `?entity=` so a link and the back button return to the same one.
 *
 * A year holds one question per workstream (GHG Emissions / Risks and Opportunities / Targets), each with its
 * own owner, approval and source documents. This page lists them; the work happens on a question's own page.
 *
 * Questions are read through `useGetQuestionsOfRound` rather than `GET /years`, because that endpoint already
 * carries the owner, the assignees and the stage — which is what the cards show, and what the year rows would
 * otherwise have to be widened to duplicate.
 */
function FoundationDataV2Page() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { userPermissions } = useAuth();

  const onError = (err) =>
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);

  const { data: roundStatus, isLoading: roundLoading } = useFoundationV2Round({ onError });
  const initialised = roundStatus?.initialised === true;
  const round = roundStatus?.round;
  const hasControllers = (round?.ownerEmails?.length ?? 0) > 0;

  const canEdit = useMemo(
    () => !!userPermissions?.some((role) => WRITE_ROLES.includes(role)),
    [userPermissions]
  );

  /*
    Auto-init on first visit, so an org never sees a dead page it cannot get out of. Guarded by a ref rather
    than by the mutation's own state, because the round query refetches and would otherwise re-trigger this
    before the first response lands.
  */
  const initAttempted = useRef(false);
  const initRound = useInitFoundationV2Round({
    onSuccess: () => queryClient.invalidateQueries(foundationV2Keys.round()),
    onError,
  });

  useEffect(() => {
    // A viewer holds no write authority, so calling /init would 401. Leave it to someone who can.
    if (roundLoading || !roundStatus || initialised || initAttempted.current || !canEdit) return;
    initAttempted.current = true;
    initRound.mutate();
  }, [roundLoading, roundStatus, initialised, canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = initialised && hasControllers;

  const { data: questions, isLoading: questionsLoading } = useGetQuestionsOfRound(null, round?.id, {
    enabled: ready && !!round?.id,
    staleTime: 0,
    onError,
  });

  const { data: years } = useFoundationV2Years({ enabled: ready, onError });
  const { data: orgEntities } = useGetOrganizationEntities({ enabled: ready, onError });
  const options = useMemo(() => entityOptions(years, orgEntities), [years, orgEntities]);
  const [searchParams, setSearchParams] = useSearchParams();
  /*
    Edits a COPY of the current params and passes the result as a value. react-router-dom is 6.3.0 here, whose
    setSearchParams accepts only a value: the `(prev) => next` callback form arrived in 6.4, and 6.3 hands a
    function to createSearchParams, which reads it as an object with no keys and navigates to a bare "?". That
    wiped `?entity=` on every pick, so the selector always snapped back to the default entity.
  */
  const updateSearchParams = (edit) => {
    const next = new URLSearchParams(searchParams);
    edit(next);
    setSearchParams(next, { replace: true });
  };
  const entityId = chooseEntity(options, searchParams.get("entity"), years);
  const selectEntity = (id) => updateSearchParams((params) => params.set("entity", id));
  const missingYears = useMemo(() => yearsMissingForEntity(years, entityId), [years, entityId]);
  const entityName = options.find((o) => o.id === entityId)?.name;

  const onWorkstreamsAdded = (created) => {
    queryClient.invalidateQueries(foundationV2Keys.years());
      // The cards come from the questions, so those are what must be refetched — the year rows are only what
      // the detail page resolves its workstream id from.
      queryClient.invalidateQueries(issbKeys.questionList());
      const count = created?.length ?? 0;
      if (count) {
        toast({
          title: `Added ${count} workstream${count === 1 ? "" : "s"}`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }
  };
  const createYear = useCreateFoundationV2Year({ onSuccess: onWorkstreamsAdded, onError });
  const addEntityToYear = useAddFoundationV2EntityToYear({ onSuccess: onWorkstreamsAdded, onError });

  const entityQuestions = useMemo(
    () => (questions || []).filter((q) => questionBelongsToEntity(q, entityId)),
    [questions, entityId]
  );
  /*
    Year filter, kept in `?year=` like the entity. Its options are the years this entity actually has, newest
    first, behind "All years" — the default, so nothing is hidden until someone picks one. A `?year=` the
    entity does not have (e.g. after switching entity) falls back to all rather than showing an empty page.
  */
  const yearOptions = useMemo(
    () => [...new Set(entityQuestions.map(yearOfQuestion).filter((y) => y > 0))].sort((a, b) => b - a),
    [entityQuestions]
  );
  const requestedYear = Number(searchParams.get("year"));
  const selectedYear = yearOptions.includes(requestedYear) ? requestedYear : null;
  const selectYear = (year) =>
    updateSearchParams((params) => {
      if (year == null) params.delete("year");
      else params.set("year", String(year));
    });
  const sections = useMemo(
    () =>
      groupQuestionsBySection(
        selectedYear == null
          ? entityQuestions
          : entityQuestions.filter((q) => yearOfQuestion(q) === selectedYear)
      ),
    [entityQuestions, selectedYear]
  );

  const openQuestion = (question) =>
    navigate(`${routes.foundationDataV2Answer}?id=${question.id}&entity=${entityId}`);

  if (roundLoading || initRound.isLoading) {
    return (
      <SecondaryHeader showRoundSelector={false} showActions={false}>
        <IssbContainer>
          <Spinner />
        </IssbContainer>
      </SecondaryHeader>
    );
  }

  // The round is created without controllers, so this is the first thing a new org sees.
  if (initialised && !hasControllers) {
    return (
      <RoundOverrideProvider round={round}>
        <FoundationV2SetupController />
      </RoundOverrideProvider>
    );
  }

  if (!initialised) {
    return (
      <SecondaryHeader showRoundSelector={false} showActions={false}>
        <IssbContainer margin="15%">
          <EmptyState
            title="Foundation Data is not set up yet"
            description="An organization admin needs to open this page once to create it."
          />
        </IssbContainer>
      </SecondaryHeader>
    );
  }

  return (
    <RoundOverrideProvider round={round}>
      <SecondaryHeader showRoundSelector={false} showActions={false}>
        <IssbContainer margin="15%">
          <Flex direction="column" gap={2}>
            <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
              <Box>
                <Text fontSize="heading.3xl" fontWeight="700" color="darkTeal">
                  Foundation Data
                </Text>
                <Text maxW="75%" color="gray.500" fontSize="body.m">
                  Prepare your sustainability data and insights — complete the key tasks and gather the
                  evidence, metrics, and plans that underpin credible sustainability reporting.
                </Text>
              </Box>
              {/* Hidden, not disabled, for a viewer: an authority denial answers 401 here. */}
              {canEdit ? (
                <AddYearControl
                  onAddYear={(year) => createYear.mutate(year)}
                  isAdding={createYear.isLoading}
                />
              ) : null}
            </Flex>
            {/* Filters, in the Insights pill style (FilterPill). The entity is a plain pill-shaped label when
                there is only one — a dropdown with nothing to choose from reads as broken. */}
            {options.length > 0 ? (
              <HStack spacing={3} wrap="wrap" mt={2}>
                {options.length > 1 ? (
                  <FilterPill
                    ariaLabel="Entity"
                    value={entityId}
                    onChange={selectEntity}
                    options={options.map((o) => ({ value: o.id, label: o.name }))}
                    placeholder="Entity"
                  />
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="full"
                    px={3}
                    h="32px"
                    display="inline-flex"
                    alignItems="center"
                  >
                    {entityName}
                  </Text>
                )}
                {yearOptions.length > 0 ? (
                  <FilterPill
                    ariaLabel="Year"
                    value={selectedYear}
                    onChange={selectYear}
                    options={[{ value: null, label: "All years" }, ...yearOptions.map((y) => ({ value: y, label: String(y) }))]}
                    placeholder="All years"
                  />
                ) : null}
                {/* An entity created after a year was added has no workstreams for it until someone adds them. */}
                {canEdit
                  ? missingYears.map((year) => (
                      <Button
                        key={year}
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        borderColor="gray.300"
                        color="gray.600"
                        fontWeight="normal"
                        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
                        isLoading={
                          addEntityToYear.isLoading && addEntityToYear.variables?.year === year
                        }
                        onClick={() => addEntityToYear.mutate({ year, entityId })}
                      >
                        + Add {entityName} to {year}
                      </Button>
                    ))
                  : null}
              </HStack>
            ) : null}
          </Flex>

          {questionsLoading ? <Spinner /> : null}

          {!questionsLoading && sections.length === 0 ? (
            <EmptyState
              title="No reporting years yet"
              description={
                canEdit
                  ? "Add a year to create its three workstreams — GHG Emissions, Risks and Opportunities, and Targets — for every entity."
                  : "An organization admin or member needs to add a reporting year first."
              }
            />
          ) : null}

          <VStack spacing={8} align="stretch">
            {sections.map((section) => (
              <Box key={section.name}>
                <HStack spacing={2} mb={3}>
                  {FOUNDATION_SECTION_ICONS[section.name]
                    ? createElement(FOUNDATION_SECTION_ICONS[section.name], {
                        size: 20,
                        color: "#718096",
                      })
                    : null}
                  <H2Dashboard>{section.name}</H2Dashboard>
                </HStack>
                <Divider mb={4} borderColor="gray.200" />
                <SectionGrid questions={section.questions} onOpen={openQuestion} />
              </Box>
            ))}
          </VStack>
        </IssbContainer>
      </SecondaryHeader>
    </RoundOverrideProvider>
  );
}

export default WithAuthorization(FoundationDataV2Page);
